import React, { useState, useEffect, useRef } from 'react';
import { FaTimes, FaInfoCircle, FaDatabase } from 'react-icons/fa';
import LoadingIndicator from './loadingIndicator';
import { useUnderlyingData } from 'store/useUnderlyingDataStore';
import { useAppSelector } from 'redux/hooks';
import { IoClose } from 'react-icons/io5';

interface UnderlyingDataComponentProps {
  isOpen: boolean;
  onClose: () => void;
  conversationId: string;
  messageId: string;
}

const UnderlyingDataComponent: React.FC<UnderlyingDataComponentProps> = ({ 
  isOpen, 
  onClose,
  conversationId,
  messageId
}) => {
  const [isInfoVisible, setIsInfoVisible] = useState<boolean>(false);
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const hasLoadedRef = useRef<boolean>(false);
  const loadingTabsRef = useRef<Set<string>>(new Set());
  
  const {
    loading,
    error,
    activeTab,
    tabs,
    dimensionNames,
    loadUnderlyingData,
    loadTabData,
    switchTab,
    clearUnderlyingData
  } = useUnderlyingData();

  // Get rawResponse directly from the store
  const rawResponse = useAppSelector((state) => state.underlyingData.rawResponse);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen && conversationId && messageId && !hasLoadedRef.current) {
      scrollToBottom();
      // Set initial loading state
      setIsInitialLoading(true);
      hasLoadedRef.current = true;
      // Load underlying data when component opens
      loadUnderlyingData(conversationId, messageId).finally(() => {
        setIsInitialLoading(false);
      });
    } else if (!isOpen) {
      // Clear data when component closes
      setIsInitialLoading(false);
      hasLoadedRef.current = false;
      loadingTabsRef.current.clear();
      clearUnderlyingData();
    }
  }, [isOpen, conversationId, messageId]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const onInformation = () => {
    setIsInfoVisible(!isInfoVisible);
  };

  // Format dimension names for display
  const formatDimensionName = (name: string) => {
    return name
      .replace(/_/g, ' ') // Replace underscores with spaces
      .toLowerCase() // Convert to lowercase first
      .replace(/\b\w/g, (char) => char.toUpperCase()) // Capitalize first letter of each word
      .trim();
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Format number with commas
  const formatNumber = (value: number | string) => {
    if (value === null || value === undefined) return '0';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return num.toLocaleString('en-US', { maximumFractionDigits: 2 });
  };

  // Get color classes for variance percentage based on positive/negative value
  const getVarianceColor = (value: number | string | null | undefined) => {
    if (value === null || value === undefined) return 'text-gray-600 bg-gray-50';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return num < 0 ? 'text-red-600 bg-red-50' : 'text-green-600 bg-green-50';
  };

  // Time Insights Display Component
  const TimeInsightsDisplay = ({ data }: { data: any[] }) => {
    if (!data || data.length === 0) return null;
    
    const insight = data[0]; // Get first (and likely only) insight record
    
    return (
      <div className="bg-white border rounded-[32px] overflow-hidden">
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-32">
            {/* Left Column - Period Details */}
            <div className="space-y-4">
              <h4 className="text-ui font-semibold text-gray-900 mb-3">Summary</h4>
              <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-meta text-gray-600">{insight.metric_name}:</span>
                  <span className="text-meta text-gray-900">{formatNumber(insight.current_value)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-meta text-gray-600">Period Start Date:</span>
                  <span className="text-meta text-gray-900">{formatDate(insight.period_start_date)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-meta text-gray-600">Period End Date:</span>
                  <span className="text-meta text-gray-900">{formatDate(insight.period_end_date)}</span>
                </div>
            
              </div>
            </div>

            {/* Right Column - Comparisons */}
            <div className="space-y-12">
              {/* Previous Period Comparison */}
              <div>
                <h4 className="text-ui font-semibold text-gray-900 mb-3">Previous Period Comparison</h4>
                <div className="space-y-2">
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-meta text-gray-600">Previous Period - Value:</span>
                    <span className="text-meta text-gray-900">{formatNumber(insight.previous_value)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-meta text-gray-600">Previous Period - Variance:</span>
                    <span className="text-meta text-gray-900">{formatNumber(insight.previous_variance)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-meta text-gray-600">Previous Period - Variance %:</span>
                    <span className={`text-meta font-semibold px-2 py-1 rounded ${getVarianceColor(insight.previous_variance_pct)}`}>
                      {insight.previous_variance_pct}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Same Period Last Year Comparison */}
              <div>
                <h4 className="text-ui font-semibold text-gray-900 mb-3">Same Period Last Year Comparison</h4>
                <div className="space-y-2">
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-meta font-medium text-gray-600">Same Period Last Year - Value:</span>
                    <span className="text-meta text-gray-900">{formatNumber(insight.same_period_last_year_value)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-meta font-medium text-gray-600">Same Period Last Year - Variance:</span>
                    <span className="text-meta text-gray-900">{formatNumber(insight.same_period_last_year_variance)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-meta font-medium text-gray-600">Same Period Last Year - Variance %:</span>
                    <span className={`text-meta font-semibold px-2 py-1 rounded ${getVarianceColor(insight.same_period_last_year_variance_pct)}`}>
                      {insight.same_period_last_year_variance_pct}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleTabClick = (tabId: string) => {
    if (!tabId || !conversationId || !messageId) return;
    
    switchTab(tabId);
    
    // For time_insights, no need to load data as it's already available
    if (tabId === 'time_insights') {
      return;
    }
    
    // For dimension tabs, ALWAYS load data on first click
    const activeTabData = tabs.find(tab => tab.id === tabId);
    
    if (activeTabData && !activeTabData.loading && !loadingTabsRef.current.has(tabId)) {
      loadingTabsRef.current.add(tabId);
      loadTabData(tabId, conversationId, messageId, dimensionNames).finally(() => {
        loadingTabsRef.current.delete(tabId);
      });
    }
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="w-full md:w-[64rem] h-screen bg-white flex flex-col shadow-lg">
        <div className="px-8 py-4 border-b flex items-center justify-between bg-secondary-background text-primary-text flex-shrink-0">
          <div className="flex items-center text-primary-text flex-1 pr-2">
            <div className="text-ui truncate">
              Underlying Data
            </div>
          </div>
          {isInfoVisible && (
            <button 
              onClick={onInformation}
              className="transition-colors flex-shrink-0 mr-2"
              aria-label="Toggle info"
            >
              <FaInfoCircle className='w-5 h-5'/>
            </button>
          )}
          <button 
            onClick={onClose}
            className="transition-colors flex-shrink-0"
            aria-label="Close underlying data"
          >
            <IoClose className='w-5 h-5'/>
          </button>
        </div>

        <div className="flex-1 overflow-auto">
          {(loading || isInitialLoading) ? (
            <div className="p-8 flex flex-col justify-center items-center">
              <LoadingIndicator />
              <p className="mt-4 text-gray-600 text-meta">Loading underlying data...</p>
            </div>
          ) : error ? (
            <div className="p-8 text-center text-red-500">
              {error}
            </div>
          ) : (
            <div className="p-3">
              {/* <div className="mb-4 text-meta text-gray-600">
                <strong>Conversation ID:</strong> {conversationId}<br/>
                <strong>Message ID:</strong> {messageId}
              </div> */}
              
              {/* Dynamic Tabs - Time Insights always first */}
              <div className="mb-6">
                <div className="border-b border-gray-200">
                  <nav className="-mb-px flex space-x-8 overflow-x-auto">
                    {/* Time Insights Tab - Always show first */}
                    <div className="flex-shrink-0">
                      <button
                        onClick={() => handleTabClick('time_insights')}
                        className={`py-2 px-1 border-b-2 font-medium text-meta transition-colors duration-200 whitespace-nowrap ${
                          activeTab === 'time_insights'
                            ? 'border-primary text-primary'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        Time Insights
                      </button>
                    </div>
                    {/* Other Dimension Tabs */}
                    {tabs && tabs.length > 0 ? tabs.map((tab) => {
                      const isActive = activeTab === tab.id;
                      return (
                        <button
                          key={tab.id}
                          onClick={() => handleTabClick(tab.id)}
                          className={`py-2 px-1 border-b-2 font-medium text-meta transition-colors duration-200 whitespace-nowrap ${
                            isActive
                              ? 'border-primary text-primary'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center space-x-2">
                            <span>{formatDimensionName(tab.name)}</span>
                            {tab.loading && (
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div>
                            )}
                          </div>
                        </button>
                      );
                    }) : (
                      <div className="text-gray-500 text-meta py-2">
                        No dimensions available
                      </div>
                    )}
                  </nav>
                </div>
              </div>

              {/* Tab Content */}
              <div className="mt-4">
                {(() => {
                  if (loading) {
                    return (
                      <div className="p-8 flex justify-center items-center">
                        <LoadingIndicator />
                        <span className="ml-2 text-gray-600">Loading dimension data...</span>
                      </div>
                    );
                  }

                  if (error) {
                    return (
                      <div className="p-8 text-center text-red-500">
                        <p className="text-lg font-semibold mb-2">Error loading data</p>
                        <p>{error}</p>
                      </div>
                    );
                  }

                  // Only show "no dimensions" message if we're not on time_insights tab
                  if ((!tabs || tabs.length === 0) && activeTab !== 'time_insights') {
                    return (
                      <div className="text-center text-gray-500 p-8">
                        <p className="text-lg font-semibold mb-2">No dimensions available</p>
                        <p>This message doesn't have any underlying data dimensions to display.</p>
                      </div>
                    );
                  }

                  // Handle time_insights tab specially
                  if (activeTab === 'time_insights') {
                    const timeInsightsData = rawResponse?.results?.find((result: any) => result.dimension_name === 'time_insights');
                    
                    if (loading) {
                      return (
                        <div className="p-8 flex justify-center items-center">
                          <LoadingIndicator />
                        </div>
                      );
                    }

                    if (timeInsightsData?.error) {
                      return (
                        <div className="p-8 text-center text-red-500">
                          {timeInsightsData.error}
                        </div>
                      );
                    }

                    if (!timeInsightsData?.data || timeInsightsData.data.length === 0) {
                      return (
                        <div className="text-center text-gray-500 p-8">
                          <div className="mb-4">
                            <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                              <FaDatabase className="text-gray-400 text-2xl" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                              No Time Insights Available
                            </h3>
                            <p className="text-meta text-gray-600">
                              No time insights data found for this message.
                            </p>
                          </div>
                        </div>
                      );
                    }

                    return <TimeInsightsDisplay data={timeInsightsData.data} />;
                  }

                  // Handle other dimension tabs
                  const activeTabData = tabs.find(tab => tab.id === activeTab);
                  if (!activeTabData) {
                    return (
                      <div className="text-center text-gray-500 p-8">
                        <p className="text-lg font-semibold mb-2">No tab data available</p>
                        <p>Please select a dimension tab to view data.</p>
                      </div>
                    );
                  }

                  if (activeTabData.loading) {
                    return (
                      <div className="p-8 flex justify-center items-center">
                        <LoadingIndicator />
                      </div>
                    );
                  }

                  if (activeTabData.error) {
                    return (
                      <div className="p-8 text-center text-red-500">
                        {activeTabData.error}
                      </div>
                    );
                  }

                  if (!activeTabData.data || activeTabData.data.length === 0) {
                    return (
                      <div className="text-center text-gray-500 p-8">
                        <div className="mb-4">
                          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                            <FaDatabase className="text-gray-400 text-2xl" />
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            No Data Available
                          </h3>
                          <p className="text-meta text-gray-600">
                            No data found for dimension: <strong>{formatDimensionName(activeTabData.name)}</strong>
                          </p>
                          <p className="text-xs text-gray-500 mt-2">
                            This dimension may not have any data in the current dataset or the query returned no results.
                          </p>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div className="bg-white border rounded-[32px] overflow-hidden">
                      <div className="p-4">
                        {/* <div className="mb-4">
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            {formatDimensionName(activeTabData.name)} Data
                          </h3>
                          <p className="text-meta text-gray-600">
                            Showing {activeTabData.data.length} records
                          </p>
                        </div> */}
                        <div className="bg-gray-50 rounded overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-100">
                              <tr>
                                {activeTabData.data.length > 0 && Object.keys(activeTabData.data[0]).map((key, idx) => (
                                  <th
                                    key={idx}
                                    className="px-4 py-3 text-left text-label text-primary-text tracking-wider"
                                  >
                                    {formatDimensionName(key)}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {activeTabData.data.map((row: Record<string, any>, rowIdx: number) => (
                                <tr
                                  key={rowIdx}
                                  // className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                                  className="bg-white border-t border-b border-tertiary-background"
                                >
                                  {Object.keys(row).map((key, colIdx) => (
                                    <td
                                      key={colIdx}
                                      className="px-4 py-3 text-ui text-primary-text max-w-xs"
                                    >
                                      <div className="truncate" title={String(row[key] ?? '')}>
                                        {typeof row[key] === 'object'
                                          ? JSON.stringify(row[key])
                                          : String(row[key] ?? '')}
                                      </div>
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {activeTabData.data.length === 0 && (
                          <div className="text-center py-8 text-gray-500">
                            No data available for {activeTabData.name}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};

export default UnderlyingDataComponent;
